import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface WorkflowErrorStateProps {
  error: string | Error | { message: string; retryable?: boolean } | null;
  onRetry?: () => void;
  compact?: boolean;
}

/**
 * WorkflowErrorState - Pure presentational component for error display.
 *
 * Single Responsibility: ONLY renders error state with optional retry.
 * Supports compact mode for inline error display.
 */
export const WorkflowErrorState: React.FC<WorkflowErrorStateProps> = ({
  error,
  onRetry,
  compact = false,
}) => {
  const errorMessage =
    typeof error === 'string' ? error : error?.message || 'Something went wrong';

  const isRetryable = typeof error === 'object' && error !== null && 'retryable' in error
    ? error.retryable !== false
    : true;

  return (
    <Box
      sx={(theme) => ({
        p: compact ? 2 : 4,
        textAlign: 'center',
        bgcolor: 'error.50',
        borderRadius: 1,
        border: 1,
        borderColor: 'error.200',
        ...theme.applyStyles('dark', { bgcolor: 'error.900' }),
      })}
    >
      <ErrorOutlineIcon
        color="error"
        sx={{ fontSize: compact ? 32 : 48, mb: 1 }}
      />
      <Typography
        variant={compact ? 'body2' : 'h6'}
        color="error.main"
        gutterBottom
      >
        {errorMessage}
      </Typography>
      {onRetry && isRetryable && (
        <Button
          variant="outlined"
          color="error"
          onClick={onRetry}
          size={compact ? 'small' : 'medium'}
          startIcon={<RefreshIcon />}
        >
          Try Again
        </Button>
      )}
    </Box>
  );
};

export default WorkflowErrorState;
